import { Stamp, FileCheck2, Scale } from "lucide-react";

/**
 * Layered legal documents with animated scan lines, stamps, evidence tags,
 * and "Litigation Ready" labels.
 */
export default function LegalDocumentationVisual() {
  return (
    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border border-zinc-200 bg-[#f7f7f5] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.12)]">
      <div className="absolute inset-0 btr-blueprint-overlay opacity-40" />

      {/* Back doc */}
      <div className="absolute top-10 left-14 w-[56%] h-[70%] bg-white rounded-md shadow-[0_8px_30px_rgba(0,0,0,0.08)] border border-zinc-200 rotate-[-6deg] overflow-hidden">
        <div className="p-5">
          <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-display">Exhibit A</div>
          <div className="h-[1px] bg-zinc-200 mt-2" />
          <div className="mt-3 space-y-1.5">
            {[100, 80, 94, 66, 82, 90].map((w, i) => (
              <div key={i} className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-zinc-300" style={{ width: `${w}%` }} />
              </div>
            ))}
          </div>
          <div className="mt-4 text-[9px] tracking-[0.2em] uppercase text-[#a2020b] font-display">
            Evidence Tag 002
          </div>
        </div>
      </div>

      {/* Middle doc */}
      <div className="absolute top-6 right-20 w-[48%] h-[74%] bg-white rounded-md shadow-[0_12px_30px_rgba(0,0,0,0.1)] border border-zinc-200 rotate-[3deg] overflow-hidden">
        <div className="p-5">
          <div className="flex items-center justify-between">
            <span className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-display">Case File — BTR</span>
            <span className="text-[9px] tracking-[0.2em] uppercase text-[#a2020b] font-display">CONFIDENTIAL</span>
          </div>
          <div className="h-[1px] bg-zinc-200 mt-2" />
          <div className="mt-3 space-y-1.5">
            {[95, 72, 88, 60, 80, 70, 92].map((w, i) => (
              <div key={i} className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-zinc-300" style={{ width: `${w}%`, opacity: i === 2 ? 1 : 0.8 }} />
              </div>
            ))}
          </div>
        </div>
        {/* Red highlighted line */}
        <div className="absolute top-[38%] left-5 right-5 h-1.5 bg-[#a2020b]/15 rounded-full" />
        <div className="absolute top-[38%] left-5 right-5 h-1.5 rounded-full overflow-hidden">
          <div className="h-full bg-[#a2020b]/70" style={{ width: "62%" }} />
        </div>

        {/* Scan line */}
        <div
          className="absolute left-0 right-0 h-[2px] bg-[#a2020b] opacity-80"
          style={{ boxShadow: "0 0 14px #a2020b", animation: "btr-scan 4.5s ease-in-out infinite" }}
        />
      </div>

      {/* Front doc */}
      <div className="absolute bottom-6 left-20 w-[46%] h-[60%] bg-white rounded-md shadow-[0_18px_40px_rgba(0,0,0,0.12)] border border-zinc-200 rotate-[-2deg] overflow-hidden">
        <div className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Scale className="w-3.5 h-3.5 text-[#a2020b]" />
              <span className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-display">Deposition File</span>
            </div>
            <span className="text-[9px] tracking-[0.2em] uppercase text-[#a2020b] font-display">SEALED</span>
          </div>
          <div className="h-[1px] bg-zinc-200 mt-2" />
          <div className="mt-3 space-y-1.5">
            {[100, 82, 74, 88, 66].map((w, i) => (
              <div key={i} className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-zinc-300" style={{ width: `${w}%` }} />
              </div>
            ))}
          </div>

          {/* Stamp */}
          <div className="mt-3 inline-flex items-center gap-1.5 border-2 border-[#a2020b] rounded-sm px-2 py-1 rotate-[-6deg]">
            <FileCheck2 className="w-3 h-3 text-[#a2020b]" />
            <span className="text-[9px] font-display font-extrabold tracking-[0.25em] uppercase text-[#a2020b]">
              Litigation Ready
            </span>
          </div>
        </div>
      </div>

      {/* Floating "EXHIBIT" stamp */}
      <div className="absolute top-5 right-5 rotate-[8deg] flex items-center gap-1.5 border-2 border-[#a2020b] rounded-sm px-2.5 py-1 bg-white/80 backdrop-blur-sm">
        <Stamp className="w-3.5 h-3.5 text-[#a2020b]" />
        <span className="text-[10px] font-display font-extrabold tracking-[0.3em] uppercase text-[#a2020b]">
          Exhibit · 07
        </span>
      </div>

      {/* Evidence tag */}
      <div className="absolute bottom-4 right-6 flex items-center gap-2 btr-tag-pulse bg-white border border-zinc-200 rounded-full px-3 py-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[#a2020b] btr-red-glow" />
        <span className="text-[10px] font-display font-bold tracking-[0.2em] uppercase text-[#a2020b]">
          Evidence Verified
        </span>
      </div>
    </div>
  );
}
